import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Loader2, Play } from 'lucide-react';
import { collection, getDocs, query, where, limit, addDoc, writeBatch, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import toast from 'react-hot-toast';

const MigrateTestData = () => {
  const [migrating, setMigrating] = useState(false);
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);

  const addLog = (message, type = 'info') => {
    setLogs(prev => [...prev, { message, type, timestamp: new Date() }]);
    console.log(`[${type.toUpperCase()}]`, message);
  };

  const migrateData = async () => {
    setMigrating(true);
    setLogs([]);
    setSummary(null);

    try {
      addLog('Starting migration of test registrations...', 'info');

      // Step 1: Get all test registrations
      const registrationsSnapshot = await getDocs(collection(db, 'admissionsTestRegistrations'));
      addLog(`Found ${registrationsSnapshot.size} test registrations`, 'info');

      if (registrationsSnapshot.empty) {
        addLog('No registrations to migrate', 'warning');
        setSummary({ total: 0, migrated: 0, skipped: 0, errors: 0 });
        return;
      }

      // Step 2: Group registrations by institutionId
      const registrationsByInstitution = {};
      registrationsSnapshot.forEach(docSnap => {
        const data = docSnap.data();
        const institutionId = data.institutionId;

        if (!institutionId) {
          addLog(`Registration ${docSnap.id} has no institutionId, skipping`, 'warning');
          return;
        }

        if (!registrationsByInstitution[institutionId]) {
          registrationsByInstitution[institutionId] = [];
        }

        registrationsByInstitution[institutionId].push({
          id: docSnap.id,
          data: data
        });
      });

      addLog(`Found registrations for ${Object.keys(registrationsByInstitution).length} institutions`, 'info');

      // Step 3: For each institution, find or create test config
      let migratedCount = 0;
      let skippedCount = 0;
      let errorCount = 0;

      for (const [institutionId, registrations] of Object.entries(registrationsByInstitution)) {
        addLog(`Processing institution: ${institutionId} (${registrations.length} registrations)`, 'info');

        try {
          // Check if registrations already have testId
          const hasTestId = registrations.some(reg => reg.data.testId);
          if (hasTestId) {
            addLog(`  ✓ Registrations already have testId, skipping`, 'success');
            skippedCount += registrations.length;
            continue;
          }

          // Find test config for this institution
          const testConfigsQuery = query(
            collection(db, 'admissionsTestConfig'),
            where('institutionId', '==', institutionId),
            limit(1)
          );
          const testConfigsSnapshot = await getDocs(testConfigsQuery);

          let testId;

          if (testConfigsSnapshot.empty) {
            addLog(`  ! No test config found, creating default...`, 'warning');

            // Create a default test config
            const newTestRef = await addDoc(collection(db, 'admissionsTestConfig'), {
              institutionId: institutionId,
              enabled: true,
              mandatory: false,
              registrationOpen: true,
              testName: 'Admission Test',
              testDate: '',
              testTime: '',
              duration: '',
              syllabus: '',
              examPattern: '',
              totalMarks: '',
              passingMarks: '',
              instructions: '',
              registrationFee: 0,
              lastDateToRegister: '',
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });

            testId = newTestRef.id;
            addLog(`  ✓ Created test config: ${testId}`, 'success');
          } else {
            testId = testConfigsSnapshot.docs[0].id;
            addLog(`  ✓ Found existing test config: ${testId}`, 'success');
          }

          // Update all registrations for this institution
          const batch = writeBatch(db);
          let batchCount = 0;

          for (const registration of registrations) {
            const regRef = doc(db, 'admissionsTestRegistrations', registration.id);
            batch.update(regRef, {
              testId: testId,
              updatedAt: serverTimestamp()
            });
            batchCount++;

            // Firestore batch limit is 500 operations
            if (batchCount >= 500) {
              await batch.commit();
              addLog(`  ✓ Committed batch of ${batchCount} updates`, 'success');
              batchCount = 0;
            }
          }

          if (batchCount > 0) {
            await batch.commit();
            addLog(`  ✓ Committed final batch of ${batchCount} updates`, 'success');
          }

          migratedCount += registrations.length;
          addLog(`  ✓ Migrated ${registrations.length} registrations`, 'success');

        } catch (error) {
          addLog(`  ✗ Error processing institution ${institutionId}: ${error.message}`, 'error');
          errorCount += registrations.length;
        }
      }

      const summaryData = {
        total: registrationsSnapshot.size,
        migrated: migratedCount,
        skipped: skippedCount,
        errors: errorCount
      };

      setSummary(summaryData);
      addLog('=== Migration Complete ===', 'info');
      addLog(`Total: ${summaryData.total}, Migrated: ${summaryData.migrated}, Skipped: ${summaryData.skipped}, Errors: ${summaryData.errors}`, 'info');

      if (errorCount === 0) {
        toast.success('Migration completed successfully!');
      } else {
        toast.error('Migration completed with errors. Check logs.');
      }

    } catch (error) {
      addLog(`Fatal error: ${error.message}`, 'error');
      toast.error('Migration failed: ' + error.message);
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Migrate Test Data</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Migrate existing test registrations to support multiple tests per institution
        </p>
      </div>

      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-300 dark:border-yellow-700 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
              ⚠️ Important: Run this migration once
            </p>
            <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
              This migration adds testId to existing test registrations. It's safe to run multiple times - 
              registrations that already have testId will be skipped.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Migration Status</h2>
          <button
            onClick={migrateData}
            disabled={migrating}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {migrating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Migrating...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Start Migration
              </>
            )}
          </button>
        </div>

        {summary && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-blue-600 dark:text-blue-400">Total</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{summary.total}</p>
              </div>
              <div>
                <p className="text-green-600 dark:text-green-400">Migrated</p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-100">{summary.migrated}</p>
              </div>
              <div>
                <p className="text-yellow-600 dark:text-yellow-400">Skipped</p>
                <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">{summary.skipped}</p>
              </div>
              <div>
                <p className="text-red-600 dark:text-red-400">Errors</p>
                <p className="text-2xl font-bold text-red-900 dark:text-red-100">{summary.errors}</p>
              </div>
            </div>
          </div>
        )}

        {logs.length > 0 && (
          <div className="bg-gray-900 rounded-lg p-4 max-h-96 overflow-y-auto">
            <h3 className="text-white font-semibold mb-2">Migration Logs</h3>
            <div className="space-y-1 font-mono text-xs">
              {logs.map((log, index) => (
                <div
                  key={index}
                  className={`flex items-start gap-2 ${
                    log.type === 'error' ? 'text-red-400' :
                    log.type === 'warning' ? 'text-yellow-400' :
                    log.type === 'success' ? 'text-green-400' :
                    'text-gray-300'
                  }`}
                >
                  {log.type === 'error' && <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />}
                  {log.type === 'success' && <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />}
                  {log.type === 'warning' && <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />}
                  <span>{log.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MigrateTestData;
